// ===============================================================
// 💎 FemaleEarningsPage — CLEAN FINAL REDESIGN
// path: src/pages/female/FemaleEarningsPage.js
// ===============================================================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, onSnapshot } from "firebase/firestore";

import { auth, db } from "../../firebase";
import "./FemaleEarningsPage.css";
import tokenImg from "../../assets/token.png";
import TopBar2 from "../../components/common/TopBar2";
import { formatNumberEn } from "../../utils/digits";
import { ENABLE_REAL_MONEY_PAYOUTS } from "../../config/featureFlags";

const TOKENS_PER_USD_PREVIEW = 100;

const CURRENCY_OPTIONS = [
  { code: "USD", symbol: "$", rateFromUsd: 1 },
  { code: "EUR", symbol: "€", rateFromUsd: 0.92 },
  { code: "SAR", symbol: "ر.س", rateFromUsd: 3.75 },
  { code: "AED", symbol: "د.إ", rateFromUsd: 3.67 },
  { code: "TRY", symbol: "₺", rateFromUsd: 32.2 },
];

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatNumber(value) {
  return formatNumberEn(toNumber(value));
}

function formatMoney(value, currencyCode = "USD", fractionDigits = 2) {
  return formatNumberEn(toNumber(value), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function getEarningsSummary(data) {
  return {
    availableTokens: toNumber(data?.earnings?.availableTokens),
    lifetimeTokens: toNumber(data?.earnings?.lifetimeTokens),
    femaleLevel: toNumber(data?.femaleLevel ?? data?.level ?? 0),
    cycleTokens: toNumber(data?.leveling?.tokens),
    activeDays: toNumber(data?.leveling?.activeDays),
  };
}

function InfoCard({ title, value, hint, tokenImgAlt, showToken = true }) {
  return (
    <div className="earnings-info-card">
      <div className="earnings-info-card__title">{title}</div>

      <div className="earnings-info-card__valueRow">
        {showToken && (
          <img
            src={tokenImg}
            className="earnings-token-icon"
            alt={tokenImgAlt}
          />
        )}
        <strong className="earnings-info-card__value">{value}</strong>
      </div>

      {hint ? <div className="earnings-info-card__hint">{hint}</div> : null}
    </div>
  );
}

export default function FemaleEarningsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [availableTokens, setAvailableTokens] = useState(0);
  const [lifetimeTokens, setLifetimeTokens] = useState(0);
  const [femaleLevel, setFemaleLevel] = useState(0);
  const [cycleTokens, setCycleTokens] = useState(0);
  const [activeDays, setActiveDays] = useState(0);

  const [selectedCurrency, setSelectedCurrency] = useState("USD");

  const [verificationStatus, setVerificationStatus] = useState("");
  const [withdrawEligibility, setWithdrawEligibility] = useState(null);

  const [eligibilityLoading, setEligibilityLoading] = useState(true);
  const [eligibleFromServer, setEligibleFromServer] = useState(false);
  const [withdrawEligibilityStatus, setWithdrawEligibilityStatus] =
    useState("none");
  const [minTokens, setMinTokens] = useState(0);
  const [eligibilityMessage, setEligibilityMessage] = useState("");

  const functions = useMemo(() => getFunctions(undefined, "us-central1"), []);
  const getWithdrawEligibility = useMemo(
    () => httpsCallable(functions, "getWithdrawEligibility"),
    [functions]
  );

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const ref = doc(db, "users", user.uid);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;

        const data = snap.data() || {};
        const summary = getEarningsSummary(data);

        setVerificationStatus(String(data?.verificationStatus || "").trim());
        setWithdrawEligibility(data?.withdrawEligibility ?? null);

        setAvailableTokens(summary.availableTokens);
        setLifetimeTokens(summary.lifetimeTokens);
        setFemaleLevel(summary.femaleLevel);
        setCycleTokens(summary.cycleTokens);
        setActiveDays(summary.activeDays);
      },
      (err) => {
        console.error("FemaleEarningsPage snapshot error:", err);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadEligibility() {
      // Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: must never call
      // getWithdrawEligibility while real-money payouts are disabled -
      // eligibility, methods, and USD/cash preview data must not be
      // fetched into the client at all.
      if (!ENABLE_REAL_MONEY_PAYOUTS) {
        setEligibilityLoading(false);
        return;
      }

      const user = auth.currentUser;
      if (!user) {
        setEligibilityLoading(false);
        return;
      }

      try {
        setEligibilityLoading(true);

        const res = await getWithdrawEligibility();
        const data = res?.data || {};

        if (cancelled) return;

        setEligibleFromServer(Boolean(data?.eligible));
        setWithdrawEligibilityStatus(
          String(data?.withdrawEligibilityStatus || "none").trim().toLowerCase()
        );
        setMinTokens(Number(data?.minWithdrawTokens ?? 0));
        setEligibilityMessage(String(data?.reason || ""));
      } catch (err) {
        console.error("getWithdrawEligibility failed:", err);

        if (cancelled) return;

        setEligibleFromServer(false);
        setWithdrawEligibilityStatus("none");
        setEligibilityMessage(
          t("earnings.eligibility.error", {
            defaultValue: "تعذر التحقق من حالة السحب الآن",
          })
        );
      } finally {
        if (!cancelled) setEligibilityLoading(false);
      }
    }

    loadEligibility();

    return () => {
      cancelled = true;
    };
  }, [getWithdrawEligibility, t]);

  const selectedCurrencyOption =
    CURRENCY_OPTIONS.find((item) => item.code === selectedCurrency) ||
    CURRENCY_OPTIONS[0];

  const availableUsd = availableTokens / TOKENS_PER_USD_PREVIEW;
  const availableConverted =
    availableUsd * selectedCurrencyOption.rateFromUsd;

  const withdrawUi = useMemo(() => {
    // Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: must be the first branch -
    // takes priority over loading/verification/eligibility state below,
    // all of which is preserved unchanged for reactivation later.
    if (!ENABLE_REAL_MONEY_PAYOUTS) {
      return {
        message: t("earnings.eligibility.payoutsDisabledMessage", {
          defaultValue: "السحب النقدي غير متاح حاليًا",
        }),
        buttonText: t("earnings.eligibility.payoutsDisabledCta", {
          defaultValue: "غير متاح حاليًا",
        }),
        disabled: true,
        variant: "warning",
      };
    }

    if (eligibilityLoading) {
      return {
        message: t("earnings.eligibility.checking", {
          defaultValue: "جارٍ التحقق من أهلية السحب...",
        }),
        buttonText: t("earnings.eligibility.checking", {
          defaultValue: "جارٍ التحقق من أهلية السحب...",
        }),
        disabled: true,
        variant: "warning",
      };
    }

    const vs = String(verificationStatus || "").trim().toLowerCase();
    if (vs !== "approved") {
      return {
        message: t("earnings.eligibility.unverifiedMessage", {
          defaultValue: "سيتم تفعيل السحب بعد اعتماد التحقق النهائي من الإدارة",
        }),
        buttonText: t("earnings.eligibility.notEligible", {
          defaultValue: "السحب غير متاح حالياً",
        }),
        disabled: true,
        variant: "warning",
      };
    }

    const firestoreWeStatus = String(
      withdrawEligibility?.status || ""
    )
      .trim()
      .toLowerCase();
    const isReadyForAdminReview =
      firestoreWeStatus === "ready_for_admin_review" ||
      withdrawEligibilityStatus === "ready_for_admin_review";

    const isApprovedForPayout =
      firestoreWeStatus === "approved_for_payout" ||
      withdrawEligibilityStatus === "approved_for_payout" ||
      eligibleFromServer === true;

    if (isReadyForAdminReview) {
      return {
        message: t("earnings.eligibility.readyForReviewMessage", {
          defaultValue: "أرباحك وصلت للحد المطلوب، وحسابك قيد مراجعة الإدارة للسحب",
        }),
        buttonText: t("earnings.eligibility.notEligible", {
          defaultValue: "قيد مراجعة الإدارة",
        }),
        disabled: true,
        variant: "warning",
      };
    }

    if (isApprovedForPayout) {
      return {
        message: t("earnings.eligibility.approvedMessage", {
          defaultValue: "تمت الموافقة على أهلية السحب، يمكنك الآن تقديم طلب السحب",
        }),
        buttonText: t("withdraw.request.title", {
          defaultValue: "طلب السحب",
        }),
        disabled: false,
        variant: "success",
      };
    }

    if (!eligibleFromServer) {
      return {
        message:
          eligibilityMessage ||
          t("earnings.eligibility.notEligible", {
            defaultValue: "السحب غير متاح حاليًا",
          }),
        buttonText: t("earnings.eligibility.notEligible", {
          defaultValue: "السحب غير متاح حالياً",
        }),
        disabled: true,
        variant: "warning",
      };
    }

    return {
      message: t("earnings.eligibility.pendingMessage", {
        defaultValue:
          "عند وصولك للحد الأدنى واستيفاء الشروط، سيتم إرسال حسابك تلقائيًا لمراجعة الإدارة",
      }),
      buttonText: t("earnings.eligibility.notEligible", {
        defaultValue: "السحب غير متاح حالياً",
      }),
      disabled: true,
      variant: "warning",
    };
  }, [
    eligibilityLoading,
    verificationStatus,
    withdrawEligibility,
    withdrawEligibilityStatus,
    eligibleFromServer,
    eligibilityMessage,
    t,
  ]);

  return (
    <div className="earnings-page">
      <TopBar2
        title={t("earnings.overview.title", { defaultValue: "الأرباح" })}
        onBack={() => navigate(-1)}
      />

      <div className="earnings-page__spacer" />

      <div className="earnings-page__content">
        <div className="earnings-hero earnings-hero--clean">
          <h1 className="earnings-hero__title">
            {t("earnings.overview.title", { defaultValue: "الأرباح" })}
          </h1>

          <p className="earnings-hero__subtitle">
            {t("earnings.overview.subtitle", {
              defaultValue:
                "تابعي الرصيد القابل للسحب وإجمالي أرباحك وتقدمك الحالي بسهولة.",
            })}
          </p>
        </div>

        <div className="earnings-top-stats earnings-top-stats--clean">
          <div className="earnings-top-stat">
            <span className="earnings-top-stat__label">
              {t("earnings.stats.femaleLevelShort", {
                defaultValue: "المستوى",
              })}
            </span>
            <strong className="earnings-top-stat__value">Lv {femaleLevel}</strong>
          </div>

          <div className="earnings-top-stat">
            <span className="earnings-top-stat__label">
              {t("earnings.stats.activeDays", {
                defaultValue: "أيام النشاط",
              })}
            </span>
            <strong className="earnings-top-stat__value">
              {formatNumber(activeDays)}
            </strong>
          </div>

          <div className="earnings-top-stat">
            <span className="earnings-top-stat__label">
              {t("earnings.stats.levelProgress", {
                defaultValue: "توكن التقدم",
              })}
            </span>
            <strong className="earnings-top-stat__value">
              {formatNumber(cycleTokens)}
            </strong>
          </div>
        </div>

        <div className="earnings-withdraw-card">
          <div className="earnings-withdraw-card__head">
            <div>
              {/* Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: the original
                  "withdrawable balance" label/hint below is preserved,
                  replaced with neutral virtual-token wording while
                  payouts are disabled. */}
              <div className="earnings-withdraw-card__label">
                {ENABLE_REAL_MONEY_PAYOUTS
                  ? t("earnings.balance.availableTokens", {
                      defaultValue: "الرصيد القابل للسحب",
                    })
                  : t("earnings.balance.tokenBalanceLabel", {
                      defaultValue: "رصيد التوكنز",
                    })}
              </div>

              <div className="earnings-withdraw-card__sub">
                {ENABLE_REAL_MONEY_PAYOUTS
                  ? t("earnings.balance.availableTokensHint", {
                      defaultValue: "هذا هو الرصيد المستخدم في السحب فقط",
                    })
                  : t("earnings.balance.tokenBalanceHint", {
                      defaultValue:
                        "توكنز افتراضية داخل التطبيق، غير قابلة للاستبدال بنقود.",
                    })}
              </div>
            </div>

            {/* Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: currency selector
                is a real-money-equivalent control - hidden while payouts
                are disabled, preserved (not deleted) for reactivation. */}
            {ENABLE_REAL_MONEY_PAYOUTS ? (
              <select
                className="earnings-currency-select"
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
              >
                {CURRENCY_OPTIONS.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.code}
                  </option>
                ))}
              </select>
            ) : null}
          </div>

          {ENABLE_REAL_MONEY_PAYOUTS ? (
            <div className="earnings-conversion-row">
              <div className="earnings-conversion-box">
                <div className="earnings-conversion-box__title">
                  {t("tokens.unit", {
                    defaultValue: "توكن",
                  })}
                </div>

                <div className="earnings-conversion-box__valueRow">
                  <img
                    src={tokenImg}
                    className="earnings-token-icon earnings-token-icon--lg"
                    alt={t("tokens.plural", { defaultValue: "Tokens" })}
                  />
                  <strong className="earnings-conversion-box__value">
                    {formatNumber(availableTokens)}
                  </strong>
                </div>
              </div>

              <div className="earnings-conversion-equal">=</div>

              <div className="earnings-conversion-box earnings-conversion-box--money">
                <div className="earnings-conversion-box__title">
                  {selectedCurrencyOption.code}
                </div>

                <div className="earnings-conversion-box__valueRow">
                  <strong className="earnings-conversion-box__value">
                    {selectedCurrencyOption.symbol}
                    {formatMoney(availableConverted, selectedCurrencyOption.code)}
                  </strong>
                </div>
              </div>
            </div>
          ) : (
            // Tokens shown as a non-redeemable virtual value only - no
            // real-currency equivalent while payouts are disabled.
            <div className="earnings-conversion-row">
              <div className="earnings-conversion-box">
                <div className="earnings-conversion-box__title">
                  {t("tokens.unit", { defaultValue: "توكن" })}
                </div>

                <div className="earnings-conversion-box__valueRow">
                  <img
                    src={tokenImg}
                    className="earnings-token-icon earnings-token-icon--lg"
                    alt={t("tokens.plural", { defaultValue: "Tokens" })}
                  />
                  <strong className="earnings-conversion-box__value">
                    {formatNumber(availableTokens)}
                  </strong>
                </div>

                <div className="earnings-conversion-box__title">
                  {t("earnings.balance.nonRedeemableNote", {
                    defaultValue: "قيمة افتراضية داخل التطبيق فقط، غير قابلة للاستبدال بنقود",
                  })}
                </div>
              </div>
            </div>
          )}

          {ENABLE_REAL_MONEY_PAYOUTS ? (
            <div className="earnings-conversion-rate">
              {t("earnings.balance.platformRate", {
                defaultValue: "سعر المنصة: كل 100 توكن = 1 دولار",
              })}
            </div>
          ) : null}

          <div className="earnings-status-strip">
            <div
              className={`earnings-status ${
                withdrawUi.variant === "success"
                  ? "earnings-status--success"
                  : "earnings-status--warning"
              }`}
            >
              <span className="earnings-status__dot">●</span>
              <span>{withdrawUi.message}</span>
            </div>
          </div>

          <div className="earnings-withdraw-card__footer">
            {/* Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: minimum-withdrawal
                line is real-money-specific - hidden while payouts are
                disabled, preserved (not deleted) for reactivation. */}
            {ENABLE_REAL_MONEY_PAYOUTS ? (
              <div className="earnings-min-line">
                {minTokens > 0
                  ? t("earnings.balance.minimumWithdrawTokens", {
                      defaultValue: "الحد الأدنى للسحب: {{count}} توكن",
                      count: formatNumber(minTokens),
                    })
                  : t("earnings.balance.minimumWithdrawDynamic", {
                      defaultValue: "الحد الأدنى للسحب يحدده السيرفر",
                    })}
              </div>
            ) : null}

            <button
              className="earnings-primary-btn"
              type="button"
              disabled={withdrawUi.disabled}
              onClick={() => {
                if (!ENABLE_REAL_MONEY_PAYOUTS) return;
                if (!withdrawUi.disabled) navigate("/withdraw-request");
              }}
            >
              {withdrawUi.buttonText}
            </button>
          </div>
        </div>

        <div className="earnings-secondary-grid">
          <InfoCard
            title={t("earnings.balance.lifetimeTokens", {
              defaultValue: "الرصيد التراكمي",
            })}
            value={`${formatNumber(lifetimeTokens)} ${t("tokens.unit", {
              defaultValue: "توكن",
            })}`}
            hint={t("earnings.balance.lifetimeDescription", {
              defaultValue: "هذا الرصيد لا ينقص عند السحب ويعكس مسيرتك الكاملة",
            })}
            tokenImgAlt={t("tokens.plural", { defaultValue: "Tokens" })}
          />

          <InfoCard
            title={t("earnings.stats.levelProgress", {
              defaultValue: "تقدمك الحالي",
            })}
            value={`${formatNumber(cycleTokens)} ${t("tokens.unit", {
              defaultValue: "توكن",
            })}`}
            hint={t("earnings.stats.levelProgressDescription", {
              defaultValue:
                "هذا الرصيد خاص بالتقدم والمستوى، وليس رصيدًا قابلًا للسحب",
            })}
            tokenImgAlt={t("tokens.plural", { defaultValue: "Tokens" })}
          />
        </div>

        <div className="earnings-links">
          <button
            className="earnings-link-card"
            onClick={() => navigate("/convert-earnings-to-coins")}
            type="button"
          >
            <div className="earnings-link-card__content">
              <div className="earnings-link-card__title">
                {t("earnings.menu.convertToCoins", {
                  defaultValue: "تحويل التوكنات إلى كوينز",
                })}
              </div>

              <div className="earnings-link-card__subtitle">
                {t("earnings.menu.convertToCoinsDescription", {
                  defaultValue: "حوّلي جزءًا من أرباحك إلى رصيد كوينز داخل التطبيق",
                })}
              </div>
            </div>

            <span className="earnings-link-card__icon">⇄</span>
          </button>

          {/* Phase PLAY-COMPLIANCE-PAYOUTS-DISABLE: Withdrawal History and
              Withdrawal Guide entry cards are cash-withdrawal entry
              points - hidden while payouts are disabled, preserved (not
              deleted) for reactivation. Tokens-to-Coins above remains
              available and unaffected. */}
          {ENABLE_REAL_MONEY_PAYOUTS ? (
            <button
              className="earnings-link-card"
              onClick={() => navigate("/withdraw-history")}
              type="button"
            >
              <div className="earnings-link-card__content">
                <div className="earnings-link-card__title">
                  {t("earnings.menu.withdrawHistory", {
                    defaultValue: "سجل السحب",
                  })}
                </div>

                <div className="earnings-link-card__subtitle">
                  {t("earnings.menu.withdrawHistoryDescription", {
                    defaultValue: "راجعي عمليات السحب السابقة",
                  })}
                </div>
              </div>

              <span className="earnings-link-card__icon">◔</span>
            </button>
          ) : null}

          {ENABLE_REAL_MONEY_PAYOUTS ? (
            <button
              className="earnings-link-card"
              onClick={() => navigate("/withdraw-help")}
              type="button"
            >
              <div className="earnings-link-card__content">
                <div className="earnings-link-card__title">
                  {t("earnings.menu.withdrawGuide", {
                    defaultValue: "تعليمات السحب",
                  })}
                </div>

                <div className="earnings-link-card__subtitle">
                  {t("earnings.menu.withdrawGuideDescription", {
                    defaultValue: "راجعي القواعد قبل السحب",
                  })}
                </div>
              </div>

              <span className="earnings-link-card__icon">?</span>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}